import React, { useContext, useState, useEffect } from "react";
import { AuthContext } from "../contexts/auth";
import PopoverComponentCustom from "../components/popover_component";
import Footer from "../components/footer";
import { Link } from "wouter";
import Fetching from "../components/fetching";
import { fetchFileDownloadList, downloadFile } from "../services/api_extractions";
import moment from "moment";
import CustomTooltipComponent from "../components/custom_tooltip_component";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";


function normalizeError(err) {
  if (!err) return null;

  if (typeof err === "object") return err;

  if (typeof err === "string") {
    try {
      return JSON.parse(err);
    } catch {
      return { message: "Errore di esportazione", exception: { detail: err } };
    }
  }

  return null;
}

function getPrettyErrorText(error) {
  const e = normalizeError(error);
  if (!e) return "Errore non disponibile.";

  return (
    e?.exception?.detail ||
    e?.message ||
    "Errore durante la generazione dell'export."
  );
}

const baseChipSx = {
  fontSize: "0.70rem",     // più piccolo
  height: 24,              // più piccolo (prima 32)
  width: 100,
  px: 1,                   // più stretto (prima 1.25)
  borderRadius: "9999px",
  border: "1px solid #6366f1",
  fontWeight: 600,
  backgroundColor: "#eef2ff",
  color: "#4338ca",
  "& .MuiChip-label": {
    px: 0.5,
    py: 0,
  },
  "& .MuiChip-icon": {
    fontSize: "0.95rem",   // icona più piccola
    ml: "6px",
    mr: "-2px",
    color: "inherit",
  },
};

function getStatusChip(status, error) {
  switch (status) {
    case "pending":
      return (
        <Chip
          icon={<HourglassEmptyIcon />}
          label="In coda"
          sx={{
            ...baseChipSx,
            backgroundColor: "#f3f4f6",
            border: "1px solid #9ca3af",
            color: "#374151",
          }}
        />
      );

    case "running":
      return (
        <Chip
          icon={<AutorenewIcon />}
          label="In corso"
          sx={{
            ...baseChipSx,
            backgroundColor: "#fef3c7",
            border: "1px solid #f59e0b",
            color: "#92400e",
          }}
        />
      );

    case "completed":
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Completo"
          sx={{
            ...baseChipSx,
            backgroundColor: "#e0e7ff",
            border: "1px solid #8b5cf6",
            color: "#5b21b6",
          }}
        />
      );

    case "failed": {
      const text = getPrettyErrorText(error);

      return (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Chip
            icon={<ErrorOutlineIcon />}
            label="Errore"
            sx={{
              ...baseChipSx,
              backgroundColor: "#fee2e2",
              border: "1px solid #ef4444",
              color: "#b91c1c",
            }}
          />

          {/* icona dedicata SOLO quando failed */}
          <CustomTooltipComponent
            title={
              <span style={{ whiteSpace: "pre-wrap" }}>
                {text}
              </span>
            }
            arrow
            placement="top"
          >
            <IconButton
              size="small"
              sx={{ color: "#b91c1c" }}
              aria-label="Dettagli errore"
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </CustomTooltipComponent>
        </div>
      );
    }

    case "expired":
      return (
        <Chip
          icon={<AccessTimeIcon />}
          label="Scaduto"
          sx={{
            ...baseChipSx,
            backgroundColor: "#e5e7eb",
            border: "1px solid #6b7280",
            color: "#374151",
          }}
        />
      );

    default:
      return <Chip label={status} sx={baseChipSx} />;
  }
}

const getQueryParamInt = (url, key) => {
  try {
    if (!url) return null;
    const u = new URL(url);
    const v = u.searchParams.get(key);
    return v ? parseInt(v, 10) : null;
  } catch {
    return null;
  }
};

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);


function DownloadList() {
  const { profile, token } = useContext(AuthContext);
  const isProfileComplete = profile.has_complete_profile;

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  // ---- FILTRI ----
  const [searchName, setSearchName] = useState("");        // input utente
  const [debouncedName, setDebouncedName] = useState("");  // valore “stabile” per la fetch
  const [createdAt, setCreatedAt] = useState("");          // YYYY-MM-DD


  // debounce sul nome (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(searchName.trim()), 300);
    return () => clearTimeout(t);
  }, [searchName]);

  // reset pagina quando cambia filtro
  useEffect(() => {
    setPage(1);
  }, [debouncedName, createdAt]);

  useEffect(() => {
    const loadDownloads = async () => {
      try {
        setLoading(true);
        setError("");

        const data = await fetchFileDownloadList(token, page, debouncedName, createdAt, 'documents');

        setRows(data.results || []);
        setCount(data.count || 0);

        const psFromNext = getQueryParamInt(data.next, "page_size");
        const psFromPrev = getQueryParamInt(data.previous, "page_size");
        const ps = psFromNext || psFromPrev;

        if (ps && Number.isFinite(ps) && ps > 0) {
          setPageSize(ps);
        } else {
          if (data.next && Array.isArray(data.results) && data.results.length > 0) {
            setPageSize(data.results.length);
          }
        }
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || "";
        const isInvalidPage =
          status === 404 && String(detail).toLowerCase().includes("invalid page");

        if (isInvalidPage) {
          setPage(1);
          return;
        }

        console.error(err);
        setError("Errore nel caricamento dei download");
      } finally {
        setLoading(false);
      }
    };

    if (token) loadDownloads();
  }, [token, page, debouncedName, createdAt]);

  const handlePageChange = (event, value) => {
    const safe = clamp(value, 1, totalPages);
    setPage(safe);
  };

  const clearFilters = () => {
    setSearchName("");
    setCreatedAt("");
  };

  return (
    <div className='flex flex-col min-h-screen'>
      <PopoverComponentCustom title="Download" />

      {profile?.customer_type === 'company' && !isProfileComplete && profile?.company?.email == profile?.the_real_user_email && (
        <div className="w-full">
          <div className="bg-orange-100 text-orange-600 p-4 mb-6">
            <p className="text-center text-sm font-semibold">
              Il profilo non è completo. <br />
              Compila i campi mancanti{' '}
              <Link
                to="/profilo/fatturazione"
                className="text-indigo-600 hover:text-indigo-800 underline"
              >
                cliccando qui
              </Link>
            </p>
          </div>
        </div>
      )}

      <main className="flex-grow pb-8">
        <div className='mx-auto w-full max-w-[1600px] px-4 md:px-6'>
          <div className="py-6">
            <p className="text-sm text-gray-500">
              Qui trovi gli export in corso di generazione e quelli pronti al download
            </p>
          </div>

          {/* ✅ BARRA FILTRI */}
          <div className="bg-white rounded-lg shadow-sm border border-indigo-100 p-4 mb-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div className="flex flex-col gap-3 md:flex-row md:items-end">
                <TextField
                  label="Cerca per nome file"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  size="small"
                  sx={{
                    minWidth: 320,

                    "& .MuiInputLabel-root": { color: "#6b7280" },
                    "&:hover .MuiInputLabel-root": { color: "#4338ca" },
                    "& .MuiInputBase-input-MuiOutlinedInput-input": { color: "#4338ca" },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#4338ca" },

                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: "#fff",
                      height: 40,
                    },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c7d2fe", borderWidth: "1px" },
                    "& .MuiOutlinedInput-root:hover fieldset": { borderColor: "#818cf8", borderWidth: "1px" },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#4f46e5",
                      borderWidth: "1px",
                    },

                    "& .MuiOutlinedInput-input, & .MuiOutlinedInput-input:focus, & .MuiOutlinedInput-input:focus-visible": {
                      outline: "none",
                      boxShadow: "none",
                    },
                    "& .MuiOutlinedInput-root:focus-within": {
                      outline: "none",
                      boxShadow: "none",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Data richiesta"
                  type="date"
                  value={createdAt || ""}       // controllato
                  onChange={(e) => setCreatedAt(e.target.value)}
                  size="small"
                  sx={{
                    minWidth: 210,

                    "& .MuiInputLabel-root": { color: "#6b7280" },
                    "&:hover .MuiInputLabel-root": { color: "#4338ca" },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#4338ca" },

                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: "#fff",
                      height: 40,
                    },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c7d2fe", borderWidth: "1px" },
                    "& .MuiOutlinedInput-root:hover fieldset": { borderColor: "#818cf8", borderWidth: "1px" },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#4f46e5",
                      borderWidth: "1px",
                    },

                    "& .MuiOutlinedInput-input, & .MuiOutlinedInput-input:focus, & .MuiOutlinedInput-input:focus-visible": {
                      outline: "none",
                      boxShadow: "none",
                    },
                    "& .MuiOutlinedInput-root:focus-within": {
                      outline: "none",
                      boxShadow: "none",
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={clearFilters}
                  sx={{
                    height: 35,
                    borderRadius: "12px",
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    borderColor: "#c7d2fe",
                    color: "#4338ca",
                    "&:hover": {
                      borderColor: "#818cf8",
                      backgroundColor: "#eef2ff",
                    },
                  }}
                >
                  Reset filtri
                </Button>
              </div>
            </div>
          </div>

          {loading && <Fetching />}
          {error && <p className="text-red-600 mb-4">{error}</p>}

          {!loading && !error && (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="download table">
                <TableHead>
                  <TableRow>
                    {["Nome file", "Data richiesta", "Data scadenza", "Stato", "Download"].map(
                      (label, idx) => (
                        <TableCell
                          key={label}
                          align={idx === 4 ? "right" : "left"}
                          sx={{
                            textTransform: "uppercase",
                            fontWeight: 600,          // semibold
                            color: "#3730a3",         // indigo-800
                            fontSize: "0.75rem",
                            letterSpacing: "0.04em",
                          }}
                        >
                          {label}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          maxWidth: 450,
                          width: 450,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        <CustomTooltipComponent title={row.file_name || "-"}>
                          <span className="block">
                            {row.file_name || "-"}
                          </span>
                        </CustomTooltipComponent>
                      </TableCell>
                      <TableCell>
                        {row.created_at ? moment(row.created_at).format("DD/MM/YYYY HH:mm") : "-"}
                      </TableCell>
                      <TableCell>
                        {row.expired_at ? moment(row.expired_at).format("DD/MM/YYYY HH:mm") : "-"}
                      </TableCell>
                      <TableCell>{getStatusChip(row.status, row.error)}</TableCell>
                      <TableCell align="right">
                        {row.download_url ? (
                          <Button
                            variant="contained"
                            size="small"
                            sx={{
                              fontSize: "0.7rem",
                              px: 2,
                              py: 0.5,
                              borderRadius: "0.375rem",
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              backgroundColor: "#4f46e5",
                              color: "#fff",
                              "&:hover": { backgroundColor: "#6366f1" },
                            }}
                            onClick={() => downloadFile(token, row.download_url, row.file_name || "export.xlsx")}
                          >
                            Scarica
                          </Button>
                        ) : (
                          <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>
                            {row.status === "pending" || row.status === "running"
                              ? "In preparazione"
                              : "Non disponibile"}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}

                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                        Nessun download presente
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {rows.length !== 0 && (
                <Stack direction="row" justifyContent="center" sx={{ p: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    shape="rounded"
                    showFirstButton
                    showLastButton
                    siblingCount={3}   // quante pagine vicine mostrare
                    boundaryCount={0}  // quante all'inizio/fine
                  />
                </Stack>
              )}
            </TableContainer>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default DownloadList;